import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Animated,
  Alert,
  Platform,
  Modal,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Svg, Path } from 'react-native-svg';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useStartupPrompt } from '@/contexts/StartupPromptContext';
import PrimaryButton from './PrimaryButton';

export default function ConsentSheet() {
  const { t } = useLanguage();
  const { acceptConsentUpdate, session } = useAuth();
  const { currentPrompt, advanceQueue } = useStartupPrompt();
  const router = useRouter();
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const slideAnim = useRef(new Animated.Value(400)).current;
  const fadeAnim = useRef(new Animated.Value(0)).current;

  const visible = currentPrompt === 'consent';

  useEffect(() => {
    if (visible) {
      setAccepted(false);
      slideAnim.setValue(400);
      fadeAnim.setValue(0);

      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.spring(slideAnim, {
          toValue: 0,
          damping: 20,
          stiffness: 300,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [visible]);

  const handleViewTerms = () => {
    router.push({
      pathname: '/webview',
      params: {
        url: 'https://www.padrinho.app/termos',
        title: t('consent.updateTitle'),
      },
    });
  };

  const handleAccept = async () => {
    if (!session?.access_token) return;

    setLoading(true);
    try {
      const result = await acceptConsentUpdate(session.access_token);
      if (result.error) {
        Alert.alert(t('consent.errorTitle'), t('consent.errorMessage'));
      } else {
        advanceQueue();
      }
    } catch {
      Alert.alert(t('consent.errorTitle'), t('consent.errorMessage'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      statusBarTranslucent
      onRequestClose={() => {}}
    >
      <View style={styles.overlay}>
        <Animated.View style={[styles.backdrop, { opacity: fadeAnim }]} />
        <Animated.View
          style={[
            styles.sheet,
            { transform: [{ translateY: slideAnim }] },
          ]}
        >
          <View style={styles.handle} />

          <View style={styles.iconContainer}>
            <Svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <Path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M11.9845 21.606C11.9845 21.606 19.6565 19.283 19.6565 12.879C19.6565 6.474 19.9345 5.974 19.3195 5.358C18.7035 4.742 12.9905 2.75 11.9845 2.75C10.9785 2.75 5.2655 4.742 4.6505 5.358C4.0345 5.974 4.3125 6.474 4.3125 12.879C4.3125 19.283 11.9845 21.606 11.9845 21.606Z"
                stroke="#015480"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <Path
                d="M9.38589 11.8746L11.2779 13.7696L15.1759 9.8696"
                stroke="#015480"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </Svg>
          </View>

          <Text style={styles.title}>{t('consent.updateTitle')}</Text>
          <Text style={styles.message}>{t('consent.updateMessage')}</Text>

          <TouchableOpacity onPress={handleViewTerms} activeOpacity={0.7} style={styles.linkButton}>
            <Text style={styles.linkText}>{t('consent.viewTerms')}</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.checkboxRow}
            onPress={() => setAccepted(!accepted)}
            activeOpacity={0.7}
          >
            <View style={[styles.checkbox, accepted && styles.checkboxChecked]}>
              {accepted && (
                <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <Path
                    d="M13.3346 4L6.0013 11.3333L2.66797 8"
                    stroke="#FFFFFF"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </Svg>
              )}
            </View>
            <Text style={styles.checkboxLabel}>{t('consent.acceptCheckbox')}</Text>
          </TouchableOpacity>

          <View style={styles.buttonSection}>
            <PrimaryButton
              title={loading ? t('consent.acceptingButton') : t('consent.acceptButton')}
              onPress={handleAccept}
              disabled={!accepted || loading}
            />
          </View>
        </Animated.View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(21, 21, 21, 0.5)',
  },
  sheet: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    paddingHorizontal: 24,
    paddingBottom: Platform.OS === 'ios' ? 40 : 32,
    paddingTop: 12,
    alignItems: 'center',
  },
  handle: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: '#DAD8CF',
    marginBottom: 24,
  },
  iconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#F0EEE4',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  title: {
    fontFamily: 'Sentient-Regular',
    fontSize: 22,
    lineHeight: 28,
    color: '#151515',
    textAlign: 'center',
    marginBottom: 10,
  },
  message: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    color: '#56554D',
    lineHeight: 19.6,
    textAlign: 'center',
    paddingHorizontal: 8,
  },
  linkButton: {
    alignSelf: 'center',
    marginTop: 16,
    paddingVertical: 8,
  },
  linkText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: '#015480',
    lineHeight: 20,
    textDecorationLine: 'underline',
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    marginTop: 20,
    width: '100%',
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#7E7D75',
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  checkboxChecked: {
    backgroundColor: '#015480',
    borderColor: '#015480',
  },
  checkboxLabel: {
    flex: 1,
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: '#151515',
    lineHeight: 18,
  },
  buttonSection: {
    width: '100%',
    marginTop: 24,
  },
});
